import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { FILTER_CODE_TO_PATH_MAP, FILTER_PATH_ORDER } from '@/constants/filter-map';

export const generateLinkHref = ({
  categoryUrlKey,
  filterState,
  isSearchPage,
  hideColorsFamily,
  colorsFamilyOptions,
  optionIdMapToRewrite,
  searchParams,
}: {
  filterState: FilterState;
  categoryUrlKey: string;
  isSearchPage: boolean;
  hideColorsFamily: boolean;
  colorsFamilyOptions: NonNullable<
    NonNullable<NonNullable<NonNullable<GetCategoryFilterOptionsQuery['products']>['aggregations']>[0]>['options']
  >;
  optionIdMapToRewrite: Record<string, Record<string, string>>;
  searchParams?: URLSearchParams;
}) => {
  const sortedFilterState = filterState.slice(0).sort((a, b) => {
    const pathKeyA = FILTER_CODE_TO_PATH_MAP[a.attributeCode];
    const pathKeyB = FILTER_CODE_TO_PATH_MAP[b.attributeCode];
    const orderA = pathKeyA && pathKeyA in FILTER_PATH_ORDER ? FILTER_PATH_ORDER[pathKeyA] : null;
    const orderB = pathKeyB && pathKeyB in FILTER_PATH_ORDER ? FILTER_PATH_ORDER[pathKeyB] : null;

    if (orderA === orderB) return 0;

    if (orderA === null && orderB !== null) return 1;

    if (orderB === null && orderA !== null) return -1;

    return orderA! - orderB!;
  });

  if (isSearchPage) {
    const nextSearParams = new URLSearchParams();
    nextSearParams.set('query', searchParams?.get('query') || '');

    for (const { attributeCode, options } of filterState) {
      const value = options[0].value;
      const attributeQuery = attributeCode === 'price' ? value.replace('_', '-') : value;
      nextSearParams.set(`${attributeCode}_filter`, attributeQuery);
    }

    return `/search.html?${nextSearParams.toString()}`;
  }

  const filterPathParts: string[] = [];
  const nextSearchParams = new URLSearchParams();

  for (const { attributeCode, options } of sortedFilterState) {
    if (attributeCode === 'custom_option_color_ids' && hideColorsFamily) {
      for (const option of options) {
        filterPathParts.push('color', option.rewrite);
      }
      continue;
    }

    if (attributeCode === 'custom_option_color_ids' && !hideColorsFamily) {
      let nextOptions = options;
      const colorValueOrder = nextOptions.reduce<Record<string, number>>((acc, cur, i) => {
        acc[cur.value] = i;

        return acc;
      }, {});
      const colorsFamilyRewriteMapToSubColorValues = colorsFamilyOptions.reduce<Record<string, string[]>>(
        (acc, cur) => {
          if (!cur) return acc;

          const colorsFamilyRewrite = cur.label?.toLowerCase().replace(/\s/g, '-');

          if (colorsFamilyRewrite) {
            acc[colorsFamilyRewrite] = cur.value.split(',');
          }

          return acc;
        },
        {},
      );
      const firstColorValue = nextOptions[0].value;
      let colorsFamilyRewrites: string[] = [];

      while (true) {
        const nextOptionValueMap = nextOptions.reduce((acc, cur) => {
          acc[cur.value] = 1;
          return acc;
        }, {});
        const matchedColorsFamilyOption = colorsFamilyOptions.find(x => {
          return (
            !!x &&
            x.count !== null &&
            x.count !== undefined &&
            x.label?.toLowerCase() !== 'all' &&
            x.count <= nextOptions.length &&
            x.value.split(',').every(valuePart => nextOptionValueMap[valuePart] === 1)
          );
        });

        if (!matchedColorsFamilyOption) {
          break;
        }

        const colorsFamilyRewrite = matchedColorsFamilyOption?.label?.toLowerCase().replace(/\s/g, '-') || '';
        const valuePartMap = matchedColorsFamilyOption.value.split(',').reduce((acc, cur) => {
          acc[cur] = 1;
          return acc;
        }, {});

        if (colorsFamilyRewrite) {
          colorsFamilyRewrites.push(colorsFamilyRewrite);
          nextOptions = nextOptions.filter(x => !valuePartMap[x.value]);
        } else {
          break;
        }
      }

      colorsFamilyRewrites.sort((a, b) => {
        return (
          Math.min(...(colorsFamilyRewriteMapToSubColorValues[a]?.map(x => colorValueOrder[x]) || [0])) -
          Math.min(...(colorsFamilyRewriteMapToSubColorValues[b]?.map(x => colorValueOrder[x]) || [0]))
        );
      });

      let pathColorRewrite = '';
      let pathColorsFamilyRewrite = '';

      if (nextOptions.length === 0 || (nextOptions.length !== 0 && firstColorValue !== nextOptions[0].value)) {
        pathColorsFamilyRewrite = colorsFamilyRewrites[0];
        colorsFamilyRewrites = colorsFamilyRewrites.slice(1);
      } else {
        pathColorRewrite = firstColorValue
          ? optionIdMapToRewrite['color']?.[firstColorValue]
          : optionIdMapToRewrite['color']?.[nextOptions[0].value];
        nextOptions = nextOptions.slice(1);
      }

      const queryColorsFamilyRewrite = colorsFamilyRewrites.join(',');
      const queryColorsRewrite = nextOptions
        .reduce<string[]>((acc, { value }) => {
          const rewrite = optionIdMapToRewrite['color']?.[value];

          if (rewrite) {
            acc.push(rewrite);
          }

          return acc;
        }, [])
        .join(',');

      if (queryColorsFamilyRewrite) {
        nextSearchParams.set(COLORS_FAMILY_KEY, queryColorsFamilyRewrite);
      }

      if (queryColorsRewrite) {
        nextSearchParams.set('color', queryColorsRewrite);
      }

      filterPathParts.push(pathColorRewrite ? 'color' : COLORS_FAMILY_KEY, pathColorRewrite || pathColorsFamilyRewrite);
      continue;
    }

    const filterPathKey = FILTER_CODE_TO_PATH_MAP[attributeCode];

    if (filterPathKey) {
      for (const option of options) {
        filterPathParts.push(filterPathKey, option.rewrite);
      }
    }
  }

  const nextQueryStr = nextSearchParams.toString();

  return `/${categoryUrlKey}/${filterPathParts.join('/')}` + (nextQueryStr ? `?${nextQueryStr}` : '');
};
